import React, { useCallback, useMemo } from 'react';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';
import type { ProductCarouselNavigationControl } from '@noths/polaris-client-ribbons-design-system';
import { ProductCarousel } from '@noths/polaris-client-ribbons-design-system';
import { toSentenceCase } from '@noths/polaris-client-utils';

import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import type { TrackingProductData } from 'src/tracking/common/products';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import type { TrackingContext } from 'src/types/TrackingContext';
import { useListingsRecommendations } from './hooks/useListingsRecommendations';

export interface RecommendedProductsProps {
  enableFreeDomesticDeliveryLabels?: boolean;
  servingConfig: RecommendationsServingConfig;
  trackingPosition: number;
  trackingTitle: string;
}

export const RecommendedProducts = ({
  enableFreeDomesticDeliveryLabels,
  servingConfig,
  trackingPosition,
  trackingTitle,
}: RecommendedProductsProps) => {
  const trackingContentType = 'Listings recommendations';

  const trackingFullTitle = useMemo(
    () => `${trackingTitle} | ${servingConfig}`,
    [trackingTitle, servingConfig],
  );

  const trackingInfo: TrackingContext = useMemo(
    () => ({
      trackingTitle: trackingFullTitle,
      trackingContentType,
      trackingPosition,
    }),
    [trackingFullTitle, trackingPosition],
  );

  const { currencyCode, productsToTrack, recommendedProducts } = useListingsRecommendations({
    servingConfig,
  });

  const numberOfItems = useMemo(() => productsToTrack.length, [productsToTrack]);

  const onNavigation = useCallback(
    (swiped: boolean, control: ProductCarouselNavigationControl) => {
      const trackingDirection = control === 'next' ? 'next' : 'previous';
      trackCarouselNavigation(swiped, trackingDirection, {
        ...trackingInfo,
        numberOfItems,
      });
    },
    [trackingInfo, numberOfItems],
  );

  const onProductClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, { index: currentIndex }: { index: number }) => {
      if (numberOfItems > 0) {
        const currentProduct: TrackingProductData | undefined = productsToTrack.find(
          (_, index) => index === currentIndex,
        );
        if (currentProduct) {
          trackProductClick(e, {
            ...trackingInfo,
            numberOfItems,
            product: currentProduct,
            productIndex: currentIndex,
          });
        }
      }
    },
    [numberOfItems, productsToTrack, trackingInfo],
  );

  const onVisible = useCallback(() => {
    if (numberOfItems > 0) {
      trackProductImpression({
        ...trackingInfo,
        products: productsToTrack,
      });
    }
  }, [numberOfItems, trackingInfo, productsToTrack]);

  if (recommendedProducts?.length) {
    return (
      <ProductCarousel
        currency={currencyCode}
        enableFreeDomesticDeliveryLabels={enableFreeDomesticDeliveryLabels}
        onNavigation={onNavigation}
        onProductClick={onProductClick}
        onVisible={onVisible}
        products={recommendedProducts}
        title={toSentenceCase(trackingTitle)}
      />
    );
  } else {
    return null;
  }
};
